/**
 * User Context Provider
 *
 * Provides access to user metadata from Clerk including policy ID and member ID.
 * This context should be used throughout the app to access user-specific data.
 */
import {
  createContext,
  useContext,
  ReactNode,
  useEffect,
  useState,
} from 'react'
import { useAuth, useOrganization, useUser } from '@clerk/clerk-react'

/**
 * User metadata stored in Clerk publicMetadata
 */
interface UserMetadata {
  policyId?: string
  memberId?: string
  enrollmentDate?: string
  enrollmentStatus?: 'pending' | 'active' | 'suspended' | 'cancelled'
}

interface Policy {
  id: string
  policy_status?: string
}

interface PoliciesResponse {
  data?: {
    policies?: Policy[]
  }
}

/**
 * User context value provided to components
 */
interface UserContextValue {
  /** Policy ID for the current user */
  policyId: string | null
  /** Member ID for the current user (format: EQX-YYYY-NNNNNN) */
  memberId: string | null
  /** Enrollment date (ISO string) */
  enrollmentDate: string | null
  /** Enrollment status */
  enrollmentStatus: UserMetadata['enrollmentStatus'] | null
  /** Whether user data is still loading */
  isLoading: boolean
  /** Whether user is authenticated */
  isAuthenticated: boolean
  /** Clerk user object (for additional data) */
  user: ReturnType<typeof useUser>['user']
}

const UserContext = createContext<UserContextValue | undefined>(undefined)

/**
 * Provider component for user context
 */
export function UserProvider({ children }: { children: ReactNode }) {
  const { user, isLoaded } = useUser()
  const { getToken } = useAuth()
  const { organization } = useOrganization()
  const [apiPolicyId, setApiPolicyId] = useState<string | null>(null)

  const metadata = user?.publicMetadata as UserMetadata | undefined

  useEffect(() => {
    if (!isLoaded || !user || metadata?.policyId) {
      return
    }

    let isCancelled = false

    const fetchPolicyId = async () => {
      try {
        const token = await getToken()
        if (!token || isCancelled) {
          return
        }

        const insuranceServiceUrl = import.meta.env.VITE_INSURANCE_SERVICE_URL
        if (!insuranceServiceUrl) {
          return
        }

        const response = await fetch(`${insuranceServiceUrl}/api/v1/policies`, {
          method: 'GET',
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
            ...(organization?.id ? { 'X-Tenant-ID': organization.id } : {}),
          },
        })

        if (!response.ok || isCancelled) {
          return
        }

        const data = (await response.json()) as PoliciesResponse
        const policies = data?.data?.policies ?? []

        const firstActivePolicy = policies.find(
          (policy) => policy.policy_status?.toLowerCase() === 'active'
        )

        if (firstActivePolicy?.id && !isCancelled) {
          setApiPolicyId(firstActivePolicy.id)
        }
      } catch {
        return
      }
    }

    void fetchPolicyId()

    return () => {
      isCancelled = true
    }
  }, [getToken, isLoaded, metadata?.policyId, organization?.id, user])

  const value: UserContextValue = {
    policyId: metadata?.policyId || apiPolicyId || null,
    memberId: metadata?.memberId || null,
    enrollmentDate: metadata?.enrollmentDate || null,
    enrollmentStatus: metadata?.enrollmentStatus || null,
    isLoading: !isLoaded,
    isAuthenticated: !!user,
    user,
  }

  return <UserContext.Provider value={value}>{children}</UserContext.Provider>
}

/**
 * Hook to access user context
 *
 * @throws {Error} if used outside of UserProvider
 *
 * @example
 * ```tsx
 * function MyComponent() {
 *   const { policyId, memberId, isLoading } = useUserContext();
 *
 *   if (isLoading) return <Loading />;
 *   if (!policyId) return <EnrollmentRequired />;
 *
 *   return <div>Policy: {policyId}</div>;
 * }
 * ```
 */
export function useUserContext() {
  const context = useContext(UserContext)
  if (!context) {
    throw new Error('useUserContext must be used within UserProvider')
  }
  return context
}

/**
 * Hook to safely access policy ID with type narrowing
 *
 * Returns null if policy ID is not available.
 * Use this when policy ID is optional for the feature.
 *
 * @example
 * ```tsx
 * const policyId = usePolicyId();
 * if (!policyId) {
 *   return <div>No policy found</div>;
 * }
 * // policyId is string here
 * ```
 */
export function usePolicyId(): string | null {
  const { policyId } = useUserContext()
  return policyId
}

/**
 * Hook to access policy ID with error if not available
 *
 * Throws an error if policy ID is not available.
 * Use this when policy ID is required for the feature.
 *
 * @throws {Error} if policy ID is not available
 *
 * @example
 * ```tsx
 * const policyId = useRequiredPolicyId();
 * // policyId is always string here
 * ```
 */
export function useRequiredPolicyId(): string {
  const { policyId, isLoading } = useUserContext()

  if (isLoading) {
    throw new Error('User data is still loading')
  }

  if (!policyId) {
    throw new Error('Policy ID not found. Please complete enrollment.')
  }

  return policyId
}

/**
 * Hook to safely access member ID
 */
export function useMemberId(): string | null {
  const { memberId } = useUserContext()
  return memberId
}
